<template>
  <div class="border bg-white-blue rounded border-info border-left-4 mb-3 no-break"
    v-bind:class="{ 'rule-not-applicable': isNotApplicable, 'future-rule': isFuture }">
    <div class="p-2 border-bottom">
      <div class="d-flex align-items-center">
        <h5 class="mb-0 text-gray-600 font-weight-bold">
          <!-- <pre>{{ JSON.stringify(rule) }}</pre> -->
          {{ rule.ruleYear ? `${rule.modality} (${rule.ruleYear})` : rule.modality }}

          <span v-if="rule.description" v-b-tooltip.hover v-bind:title="rule.description">
            <BIcon icon="info-circle" class="no-print ml-3" />
          </span>

          <span>
            <BIcon v-if="isNotApplicable" icon="x-circle" class="text-danger ml-3" />
            <!-- <BIcon v-else-if="isFuture" icon="arrow-right-circle-fill" class="text-warning ml-3" /> -->
            <BIcon v-else-if="!isFuture" icon="check-circle" class="text-success ml-3" />
          </span>

          <!-- #539 (Opção A, Ju 11/09): motivo explícito quando o motor grava
               notApplicableReason (ex.: incapacidade posterior à data do cálculo).
               Sem motivo => nada muda (comportamento de hoje). -->
          <span class="ml-3 font-weight-bold text-danger small" v-if="isNotApplicable && rule.notApplicableReason">Não aplicável: {{ rule.notApplicableReason.message }}</span>

          <!-- <span class="ml-3 font-weight-bold text-success small" v-if="isFuture && ageToRetire">Possível aposentadoria em {{ageToRetire}} </span> -->

          <h6 v-if="rule.description" class="show-only-print mb-0 mt-2">
            <BIcon icon="info-circle" />
            {{ rule.description }}
          </h6>
        </h5>
        <h6 v-if="rule.ruleLaw" class="mb-0 text-gray-500 font-weight-bold small ml-auto">({{ rule.ruleLaw }})</h6>
      </div>
      <div v-if="rule.observation">
        <span v-b-tooltip.hover v-bind:title="rule.observation">
          <BIcon icon="info-circle" class="no-print text-gray-500 ml-auto" />
        </span>
        <h6 class="show-only-print mb-0 text-gray-500 font-weight-bold small ml-auto">({{ rule.observation }})</h6>
      </div>
    </div>

    <div class="d-flex align-items-center">
      <h5 class="mb-0 text-gray-600 font-weight-bold">
        <span>
          <BIcon v-if="isFuture && whenToRetire" icon="arrow-right-circle-fill" class="text-warning ml-3" />
        </span>

        <span class="ml-3 font-weight-bold text-success small" v-if="retiredDate && !isExcludedApplicable && !whenToRetire">Possível aposentadoria em {{
          retiredDate }}</span>
        <span class="ml-3 font-weight-bold text-success small" v-if="isFuture && whenToRetire">Possível aposentadoria em
          {{ whenToRetire.format('DD/MM/YYYY') }} </span>
        <span class="ml-3 font-weight-bold text-success small" v-if="hasExclusion">
          <span v-if="isExcludedApplicable">
            Cumpriu em {{ formattedApplicableAt }}. Tornou-se não aplicável devido à existência de uma regra compulsória já aplicável.
          </span>
          <span v-if="exclusionSource === 'future' && whenToRetire">
            Cumpriria em {{ whenToRetire.format('DD/MM/YYYY') }}. Tornou-se não aplicável devido à existência de uma regra compulsória aplicável anterior.
          </span>
        </span>
      </h5>
    </div>

    <div>
      <div>
        <div class="row align-items-center">
          <div class="col-md-2">
            <div class="p-3">
              <h6 class="text-uppercase text-gray-600 small">Gênero</h6>
              <!-- <TextInfo v-bind:text="gender" /> -->
              <h6 class="mb-0 font-weight-bold text-info">{{ rule.gender }}</h6>
            </div>
          </div>

          <div class="col-md-3">
            <div class="p-3" v-if="rule.displayNecessaryAge">
              <h6 class="text-uppercase text-gray-600 small">Idade necessária</h6>
              <div class="d-flex align-items-center">
                <template v-if="rule.decreasedNecessaryAge">
                  <h6 class="mb-0 font-weight-bold text-gray mr-2">
                    <strike> {{ rule.necessaryAge }} anos</strike>
                  </h6>
                  <h6 class="mb-0 font-weight-bold text-info">
                    {{ rule.displayNecessaryAge }} anos{{ rule.displayNecessaryAgeMonths ? ` e ${rule.displayNecessaryAgeMonths} meses` : '' }}{{ rule.displayNecessaryAgeDays ? ` e ${rule.displayNecessaryAgeDays} dias` : '' }}
                  </h6>
                </template>
                <template v-else>
                  <h6 class="mb-0 font-weight-bold text-info">{{ rule.displayNecessaryAge }} anos {{ rule.displayNecessaryAgeMonths ? `e ${rule.displayNecessaryAgeMonths} meses` : '' }}</h6>
                </template>
                <h6 class="mb-0 font-weight-bold text-success small ml-2">({{ formattedCurrentAge }}) </h6>
              </div>
              <h6 class="mb-0 font-weight-bold text-success small mt-2" v-if="ageToRetire && !isNotApplicable">Completará em
                {{ ageToRetire.format('DD/MM/YYYY') }} </h6>
                <h6 class="mb-0 font-weight-bold text-success small mt-2" v-if="reachedAgeDateIgnoringStartDate && rule.isReductorRule">Completou em
                  {{ reachedAgeDateIgnoringStartDate.format('DD/MM/YYYY') + ' com ' + rule.displayNecessaryAge + ' anos' + (rule.displayNecessaryAgeMonths ? ` e ${rule.displayNecessaryAgeMonths} meses` : '') + (rule.displayNecessaryAgeDays ? ` e ${rule.displayNecessaryAgeDays} dias` : '') }} </h6>
              <h6 class="mb-0 font-weight-bold text-success small mt-2" v-if="reachedAgeDateIgnoringStartDate && !rule.isReductorRule">Completou em
                {{ reachedAgeDateIgnoringStartDate.format('DD/MM/YYYY') }} </h6>
            </div>
            <div class="p-3" v-else>
              <h6 class="text-uppercase text-gray-600 small">Idade necessária</h6>
              <div class="d-flex align-items-center">
                <h6 class="mb-0 font-weight-bold text-gray-400">Não aplicável</h6>
              </div>
            </div>
          </div>

          <div class="col-md-3">
            <div class="p-3">
              <h6 class="text-uppercase text-gray-600 small">Contribuição necessária</h6>
              <div class="d-flex align-items-center">
                <h6 class="mb-0 font-weight-bold text-info" v-if="rule.necessaryContribution">
                  {{ rule.necessaryContribution }} anos
                  {{ increasedContribution }}</h6>
                <h6 class="mb-0 font-weight-bold text-gray-400" v-else>Não aplicável</h6>
              </div>
              <!-- <h6 class="mb-0 font-weight-bold text-success small mt-2" v-if="rule.increasedNecessaryContribution">Com pedágio: {{rule.increasedNecessaryContribution}} anos</h6> -->
              <h6 class="mb-0 font-weight-bold text-success small mt-2" v-if="rule.necessaryContribution">
                ({{ contributionTime }})
              </h6>
              <h6 class="mb-0 font-weight-bold text-success small mt-2"
                v-if="rule.necessaryContribution && contributionToRetire && !isNotApplicable">
                Completará em {{ contributionToRetire.format('DD/MM/YYYY') }} </h6>
              <h6 class="mb-0 font-weight-bold text-success small mt-2" v-if="reachedContributionDateIgnoringStartDate && !isFuture && !isNotApplicable">Completou em
                {{ reachedContributionDateIgnoringStartDate.format('DD/MM/YYYY') }} </h6>
            </div>
          </div>

          <div class="col-md-2">
            <div class="p-3">
              <h6 class="text-uppercase text-gray-600 small">Tempo de Cargo</h6>
              <div class="d-flex align-items-center">
                <h6 class="mb-0 font-weight-bold text-info" v-if="rule.necessaryPositionTime">
                  {{ rule.necessaryPositionTime }} anos
                </h6>
                <h6 class="mb-0 font-weight-bold text-gray-400" v-else>Não aplicável</h6>
              </div>
              <h6 class="mb-0 font-weight-bold text-success small mt-2" v-if="rule.necessaryPositionTime">
                ({{ positionTime }})</h6>
              <h6 class="mb-0 font-weight-bold text-success small mt-2"
                v-if="rule.necessaryPositionTime && positionToRetire && !isNotApplicable">
                Completará em {{ positionToRetire.format('DD/MM/YYYY') }} </h6>
              <h6 class="mb-0 font-weight-bold text-success small mt-2" v-if="reachedPositionDateIgnoringStartDate">Completou em
                {{ reachedPositionDateIgnoringStartDate.format('DD/MM/YYYY') }} </h6>
            </div>
          </div>

          <div class="col-md-2">
            <div class="p-3">
              <h6 class="text-uppercase text-gray-600 small">Serviço Público</h6>
              <div class="d-flex align-items-center">
                <h6 class="mb-0 font-weight-bold text-info" v-if="rule.necessaryPublicServiceTime">
                  {{ rule.necessaryPublicServiceTime }} anos</h6>
                <h6 class="mb-0 font-weight-bold text-gray-400" v-else>Não aplicável</h6>
              </div>
              <h6 class="mb-0 font-weight-bold text-success small mt-2" v-if="rule.necessaryPublicServiceTime">
                ({{ publicServiceTime }})</h6>
              <h6 class="mb-0 font-weight-bold text-success small mt-2"
                v-if="rule.necessaryPublicServiceTime && publicServiceToRetire && !isNotApplicable">Completará em {{
                  publicServiceToRetire.format('DD/MM/YYYY') }} </h6>
              <h6 class="mb-0 font-weight-bold text-success small mt-2" v-if="reachedPublicServiceDateIgnoringStartDate">Completou em
                {{ reachedPublicServiceDateIgnoringStartDate.format('DD/MM/YYYY') }} </h6>
            </div>
          </div>
        </div>

        <div class="row align-items-center">
          <div class="col-md-2">
            <div class="p-3">
              <h6 class="text-uppercase text-gray-600 small">Magistério</h6>
              <div class="d-flex align-items-center">
                <h6 class="mb-0 font-weight-bold text-info" v-if="rule.necessaryTeachingTime">
                  {{ rule.necessaryTeachingTime }}
                  anos {{ increasedTeachingTime }}</h6>
                <h6 class="mb-0 font-weight-bold text-gray-400" v-else>Não aplicável</h6>
              </div>
              <h6 class="mb-0 font-weight-bold text-success small mt-2" v-if="rule.necessaryTeachingTime">
                ({{ teachingTime }})
              </h6>
              <h6 class="mb-0 font-weight-bold text-success small mt-2"
                v-if="rule.necessaryTeachingTime && teachingToRetire && !isNotApplicable">
                Completará em {{ teachingToRetire.format('DD/MM/YYYY') }} </h6>
              <h6 class="mb-0 font-weight-bold text-success small mt-2" v-if="reachedTeachingDateIgnoringStartDate">Completou em
                {{ reachedTeachingDateIgnoringStartDate.format('DD/MM/YYYY') }} </h6>
            </div>
          </div>

          <div class="col-md-3">
            <div class="p-3">
              <h6 class="text-uppercase text-gray-600 small">Tempo de Carreira</h6>
              <div class="d-flex align-items-center">
                <h6 class="mb-0 font-weight-bold text-info" v-if="rule.necessaryCarreerTime">
                  {{ rule.necessaryCarreerTime }} anos
                </h6>
                <h6 class="mb-0 font-weight-bold text-gray-400" v-else>Não aplicável</h6>
              </div>
              <h6 class="mb-0 font-weight-bold text-success small mt-2" v-if="rule.necessaryCarreerTime">
                ({{ carreerTime }})</h6>
              <h6 class="mb-0 font-weight-bold text-success small mt-2"
                v-if="rule.necessaryCarreerTime && carreerToRetire && !isNotApplicable">
                Completará em {{ carreerToRetire.format('DD/MM/YYYY') }} </h6>
              <h6 class="mb-0 font-weight-bold text-success small mt-2" v-if="reachedCarreerDateIgnoringStartDate">Completou em
                {{ reachedCarreerDateIgnoringStartDate.format('DD/MM/YYYY') }} </h6>
            </div>
          </div>

          <div class="col-md-3">
            <div class="p-3">
              <h6 class="text-uppercase text-gray-600 small">Pontos</h6>
              <div class="d-flex align-items-center">
                <h6 class="mb-0 font-weight-bold text-info" v-if="rule.necessaryPoints">{{ rule.necessaryPoints }} pontos
                  {{ formattedRuleEndDate ? `(até ${formattedRuleEndDate})` : '' }}
                </h6>
                <h6 class="mb-0 font-weight-bold text-gray-400" v-else>Não aplicável</h6>
              </div>
              <h6 class="mb-0 font-weight-bold text-success small mt-2" v-if="rule.necessaryPoints">{{ pointsDisplayText }}
              </h6>
              <h6 class="mb-0 font-weight-bold text-success small mt-2" v-if="rule.necessaryPoints && pointsToRetire && !isNotApplicable">
                Completará
                <!-- Exigência de pontos vem do BACKEND (rule.necessaryPoints, já capada na
                     tabela certa) — recomputar no front com a cópia local produzia
                     "com 105" (tabela default) quando o regime não existia na cópia. -->
                em {{ pointsToRetire.format('DD/MM/YYYY') }} com {{ rule.necessaryPoints || getNecessaryPointsTable(pointsToRetire.year()) }} </h6>
              <h6 class="mb-0 font-weight-bold text-success small mt-2" v-if="reachedPointsDate">Completou em
                {{ reachedPointsDate.format('DD/MM/YYYY') }} </h6>
            </div>
          </div>

          <div class="col-md-2">
            <div class="p-3">
              <h6 class="text-uppercase text-gray-600 small">Tempo Especial</h6>
              <div class="d-flex align-items-center">
                <h6 class="mb-0 font-weight-bold text-info" v-if="rule.necessarySpecialTime">
                  {{ rule.necessarySpecialTime }} anos
                </h6>
                <h6 class="mb-0 font-weight-bold text-gray-400" v-else>Não aplicável</h6>
              </div>
              <h6 class="mb-0 font-weight-bold text-success small mt-2" v-if="rule.necessarySpecialTime">
                ({{ specialTime }})</h6>
              <h6 class="mb-0 font-weight-bold text-success small mt-2"
                v-if="rule.necessarySpecialTime && specialTimeToRetire && !isNotApplicable">
                Completará em {{ specialTimeToRetire.format('DD/MM/YYYY') }} </h6>
              <h6 class="mb-0 font-weight-bold text-success small mt-2" v-if="reachedSpecialTimeDateIgnoringStartDate">Completou em
                {{ reachedSpecialTimeDateIgnoringStartDate.format('DD/MM/YYYY') }} </h6>
            </div>
          </div>

          <div class="col-md-2">
            <div class="p-3">
              <h6 class="text-uppercase text-gray-600 small">Tempo Polícia</h6>
              <div class="d-flex align-items-center">
                <h6 class="mb-0 font-weight-bold text-info" v-if="rule.necessaryPoliceTime">
                  {{ rule.necessaryPoliceTime }} anos
                </h6>
                <h6 class="mb-0 font-weight-bold text-gray-400" v-else>Não aplicável</h6>
              </div>
              <h6 class="mb-0 font-weight-bold text-success small mt-2" v-if="rule.necessaryPoliceTime">
                ({{ policeTime }})</h6>
              <h6 class="mb-0 font-weight-bold text-success small mt-2"
                v-if="rule.necessaryPoliceTime && policeToRetire && !isNotApplicable">
                Completará em {{ policeToRetire.format('DD/MM/YYYY') }} </h6>
              <h6 class="mb-0 font-weight-bold text-success small mt-2" v-if="reachedPoliceDateIgnoringStartDate">Completou em
                {{ reachedPoliceDateIgnoringStartDate.format('DD/MM/YYYY') }} </h6>
            </div>
          </div>

          <div class="col-md-2" v-if="rule.minRemunerations">
            <div class="p-3">
              <h6 class="text-uppercase text-gray-600 small">
                Carência
              </h6>
              <div class="d-flex align-items-center">
                <h6 class="mb-0 font-weight-bold text-info" v-if="rule.minRemunerations">
                  {{ rule.minRemunerations }} meses {{ remunerationType }}
                </h6>
                <h6 class="mb-0 font-weight-bold text-gray-400" v-else>Não aplicável</h6>
              </div>
              <h6 class="mb-0 font-weight-bold text-success small mt-2" v-if="rule.minRemunerations">
                ({{ remunerations }})
              </h6>
              <h6 class="mb-0 font-weight-bold text-success small mt-2" v-if="rule.minRemunerations && remunerationsToRetire && !isNotApplicable">
                Completará em {{ remunerationsToRetire.format('DD/MM/YYYY') }}
              </h6>
              <h6 class="mb-0 font-weight-bold text-success small mt-2" v-if="reachedRemunerationsDate">Completou em
                {{ reachedRemunerationsDate.format('DD/MM/YYYY') }} </h6>
            </div>
          </div>

          <div class="col-12">
            <div class="p-3">
              <h6 class="text-uppercase text-gray-600 small mb-2" v-b-tooltip.hover
                v-bind:title="rule.benefitCalculusMethod">
                Cálculo do Benefício
                <b-icon class="h6 mb-0" icon="exclamation-circle-fill" variant="info"></b-icon>
              </h6>
              <div class="d-flex flex-column">
                <template v-if="hasSalaries && (rule.estimatedBenefit != null || (isFuture && rule.estimatedBenefitsByFutureType && Object.keys(rule.estimatedBenefitsByFutureType).length))">
                  <template v-if="isFuture && rule.regimeType !== 'RPPS' && rule.estimatedBenefitsByFutureType && Object.keys(rule.estimatedBenefitsByFutureType).length > 0">
                    <div class="benefit-grid d-flex flex-wrap align-items-stretch">
                      <div
                        v-for="(value, type) in rule.estimatedBenefitsByFutureType"
                        :key="type"
                        role="button"
                        tabindex="0"
                        class="benefit-cell small d-flex flex-column align-items-center justify-content-center p-2 rounded cursor-pointer"
                        @click.stop="openBenefitReport(type)"
                        @keydown.enter.prevent="openBenefitReport(type)"
                        @keydown.space.prevent="openBenefitReport(type)"
                      >
                        <span class="benefit-cell-label text-muted">{{ futureSalaryTypeLabel(type) }}</span>
                        <span class="benefit-cell-value font-weight-bold text-success text-nowrap">R$ {{ formatMoney(value != null ? value : 0) }}</span>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <span v-if="isFuture && rule.regimeType === 'RPPS'" class="benefit-cell-label text-muted small mb-1">Repetir última contribuição</span>
                    <span role="button" tabindex="0" class="font-weight-bold text-success cursor-pointer p-2 rounded" @click.stop="openBenefitReport(null)" @keydown.enter.prevent="openBenefitReport(null)" @keydown.space.prevent="openBenefitReport(null)">
                      R$ {{ formatMoney(rule.estimatedBenefit) }}
                    </span>
                  </template>
                </template>
                <router-link v-else class="text-primary" v-bind:to="{ name: 'work-records' }">
                  Incluir registro de trabalho
                </router-link>
              </div>
            </div>
          </div>

          <div v-if="rule.policeRuleObservation" class="col-md-10">
            <div class="p-3">
              <h6 class="text-uppercase text-gray-600 small" v-b-tooltip.hover>
                Observação
              </h6>
              <h6 class="mb-0 font-weight-bold text-success">{{ rule.policeRuleObservation}}</h6>
            </div>
          </div>


        </div>

      </div>
    </div>

    <RuleBenefitReportModal 
      v-if="showBenefitReportModal" 
      :rule="rule"
      :isFuture="isFuture"
      :retiredDate="retiredDateParam"
      :selectedFutureSalaryType="benefitReportFutureType"
      v-on:hidden="handlerCloseModal" 
    />
  </div>
</template>

<script>
import RuleBenefitReportModal from './RuleBenefitReportModal.vue';
import moment from 'moment';
import { computeReachedRemunerationsDate } from '../utils/reachedRemunerationsDate';
import {
  getMainDisabledTimeWithConversion,
  getMainSpecialTimeWithConversion,
  getRegimeDate,
  getTime,
  calculateToll,
  specificTolls,
  getNecessaryPointsTable,
  calculateReachedContributionDate,
  calculateReachedTeachingDate,
  calculateReachedAgeDate,
  calculateReachedPositionDate,
  calculateReachedPublicServiceDate,
  calculateReachedPoliceDate,
  calculateReachedCarreerDate,
  calculateReachedSpecialTimeDate,
  calculateReachedContributionDateIgnoringStartDate,
  calculateReachedDates,
  calculateReachedPointsDate,
  calculateRetiredDate,
} from '../utils/common';

export default {
  components: { RuleBenefitReportModal },

  props: {
    rule: Object,
    isNotApplicable: Boolean,
    isFuture: Boolean,
  },

  data: () => ({
    showBenefitReportModal: false,
    benefitReportFutureType: null,
  }),

  computed: {
    retiredDateParam() {
      try {
        if (this.isFuture) {
          return this.whenToRetire ? this.whenToRetire.format('YYYY-MM-DD') : null;
        }
        if (this.retiredDate) {
          const d = (this.retiredDate && typeof this.retiredDate === 'string')
            ? require('moment')(this.retiredDate, 'DD/MM/YYYY', true)
            : null;
          return d && d.isValid() ? d.format('YYYY-MM-DD') : null;
        }
        return null;
      } catch (e) {
        return null;
      }
    },

    hasExclusion() {
      return !!(this.rule && this.rule.exclusion);
    },
    exclusionSource() {
      return this.hasExclusion ? this.rule.exclusion.source : null;
    },
    isExcludedApplicable() {
      return this.exclusionSource === 'applicable';
    },

    hasSalaries: function () {
      return this.rule.workHistory.some(workRecord => workRecord.salaries && workRecord.salaries.some(salary => salary.value > 0));
    },

    lawDate: function () {
      return this.rule.ruleLimitDate ? moment(this.rule.ruleLimitDate) : moment();
    },

    currentAge: function () {
      return this.lawDate.diff(moment(this.rule?.customer?.birthDate), 'years');
    },

    increasedContribution: function () {
      const { increasedNecessaryContribution, increasedNecessaryContributionMonths, increasedNecessaryContributionDays, necessaryContribution } = this.rule;

      let text = "";

      let increasedYear = increasedNecessaryContribution > 0;
      let increasedMonth = increasedNecessaryContributionMonths > 0;
      let increasedDay = increasedNecessaryContributionDays > 0;

      if (increasedYear) {
        text += `+ ${Math.floor(increasedNecessaryContribution - necessaryContribution)} anos`;
      }

      if (increasedMonth) {
        text += (increasedYear) ? `${(increasedDay) ? "," : " e"} ${Math.floor(increasedNecessaryContributionMonths)} meses` : `+ ${Math.floor(increasedNecessaryContributionMonths)} meses`;
      }

      if (increasedDay) {
        text += (increasedYear || increasedMonth) ? ` e ${Math.floor(increasedNecessaryContributionDays)} dias` : `+ ${Math.floor(increasedNecessaryContributionDays)} dias`;
      }

      return text;
    },

    increasedTeachingTime: function () {
      const { increasedNecessaryTeachingTime, increasedNecessaryTeachingTimeMonths, increasedNecessaryTeachingTimeDays, necessaryTeachingTime } = this.rule;

      let text = "";

      let increasedYear = increasedNecessaryTeachingTime > 0;
      let increasedMonth = increasedNecessaryTeachingTimeMonths > 0;
      let increasedDay = increasedNecessaryTeachingTimeDays > 0;

      if (increasedYear) {
        text += `+ ${Math.floor(increasedNecessaryTeachingTime - necessaryTeachingTime)} anos`;
      }

      if (increasedMonth) {
        text += (increasedYear) ? `${(increasedDay) ? "," : " e"} ${Math.floor(increasedNecessaryTeachingTimeMonths)} meses` : `+ ${Math.floor(increasedNecessaryTeachingTimeMonths)} meses`;
      }

      if (increasedDay) {
        text += (increasedYear || increasedMonth) ? ` e ${Math.floor(increasedNecessaryTeachingTimeDays)} dias` : `+ ${Math.floor(increasedNecessaryTeachingTimeDays)} dias`;
      }

      return text;
    },

    formattedCurrentAge: function () {
      return this.rule.ruleLimitDate ? `${this.currentAge} em ${moment(this.rule.ruleLimitDate).format("DD/MM/YYYY")}` : `atual ${this.currentAge}`;
    },

    totalPoints: function () {
      const {
        years: contributionTimeYears,
      } = getTime(this.rule?.workHistory, null, null, this.rule?.gender, this.rule?.regimeDate);

      return this.currentAge + (contributionTimeYears || 0) || 'indefinido';
    },

    contributionTime: function () {
      const workRecords = this.rule.workHistory;
      const regimeDate = this.rule?.regimeDate;

      let field = null;

      const isDisabledRule = this.rule.modality.includes("Deficiência");
      if (isDisabledRule) {
        let level = null;

        if (this.rule.modality.includes("Leve")) level = "low";
        if (this.rule.modality.includes("Moderada")) level = "medium";
        if (this.rule.modality.includes("Grave")) level = "high";

        field = "isDisabled";

        if (level) {
          field = `${field}-${level}`;
        }

        if (this.rule.tollLimitDate) {
          const disabledLowTimeGeralToll = getTime(workRecords, moment(this.rule.tollLimitDate), 'isDisabled-low', this.rule.gender, regimeDate);
          const disabledMediumTimeGeralToll = getTime(workRecords, moment(this.rule.tollLimitDate), 'isDisabled-medium', this.rule.gender, regimeDate);
          const disabledHighTimeGeralToll = getTime(workRecords, moment(this.rule.tollLimitDate), 'isDisabled-high', this.rule.gender, regimeDate);
          const comumContributionGeralToll = getTime(workRecords, moment(this.rule.tollLimitDate), 'comum', this.rule.gender, regimeDate, true);
          const mainDisabledTimeGeralToll = getMainDisabledTimeWithConversion(this.rule.gender, disabledLowTimeGeralToll, disabledMediumTimeGeralToll, disabledHighTimeGeralToll, comumContributionGeralToll);

          if (mainDisabledTimeGeralToll.mainDisabledLevel && mainDisabledTimeGeralToll.mainDisabledLevel == level) {
            return `${mainDisabledTimeGeralToll.years || "0"} anos, ${mainDisabledTimeGeralToll.months || "0"} meses e ${mainDisabledTimeGeralToll.days || "0"} dias em ${moment(this.rule.tollLimitDate).format("DD/MM/YYYY")} - Tempo Predominante`;
          }
        }

        if (this.rule.ruleLimitDate) {
          const disabledLowTimeGeralLimitDate = getTime(workRecords, moment(this.rule.ruleLimitDate), 'isDisabled-low', this.rule.gender, regimeDate);
          const disabledMediumTimeGeralLimitDate = getTime(workRecords, moment(this.rule.ruleLimitDate), 'isDisabled-medium', this.rule.gender, regimeDate);
          const disabledHighTimeGeralLimitDate = getTime(workRecords, moment(this.rule.ruleLimitDate), 'isDisabled-high', this.rule.gender, regimeDate);
          const comumContributionGeralLimitDate = getTime(workRecords, moment(this.rule.ruleLimitDate), 'comum', this.rule.gender, regimeDate, true);
          const mainDisabledTimeGeralLimitDate = getMainDisabledTimeWithConversion(this.rule.gender, disabledLowTimeGeralLimitDate, disabledMediumTimeGeralLimitDate, disabledHighTimeGeralLimitDate, comumContributionGeralLimitDate);

          if (mainDisabledTimeGeralLimitDate.mainDisabledLevel && mainDisabledTimeGeralLimitDate.mainDisabledLevel == level) {
            return `${mainDisabledTimeGeralLimitDate.years || "0"} anos, ${mainDisabledTimeGeralLimitDate.months || "0"} meses e ${mainDisabledTimeGeralLimitDate.days || "0"} dias em ${moment(this.rule.ruleLimitDate).format("DD/MM/YYYY")} - Tempo Predominante`;
          }
        }

        const disabledLowTimeGeral = getTime(workRecords, null, 'isDisabled-low', this.rule.gender, regimeDate);
        const disabledMediumTimeGeral = getTime(workRecords, null, 'isDisabled-medium', this.rule.gender, regimeDate);
        const disabledHighTimeGeral = getTime(workRecords, null, 'isDisabled-high', this.rule.gender, regimeDate);
        const comumContributionGeral = getTime(workRecords, null, 'comum', this.rule.gender, regimeDate, true);
        const mainDisabledTimeGeral = getMainDisabledTimeWithConversion(this.rule.gender, disabledLowTimeGeral, disabledMediumTimeGeral, disabledHighTimeGeral, comumContributionGeral);

        if (mainDisabledTimeGeral.mainDisabledLevel && mainDisabledTimeGeral.mainDisabledLevel == level) {
          return `atual ${mainDisabledTimeGeral.years || "0"} anos, ${mainDisabledTimeGeral.months || "0"} meses e ${mainDisabledTimeGeral.days || "0"} dias - Tempo Predominante`;
        }
      }

      if (this.rule.tollLimitDate) {
        const { years, months, days } = getTime(workRecords, moment(this.rule.tollLimitDate), field, this.rule.gender, regimeDate);

        return `${years || "0"} anos, ${months || "0"} meses e ${days || "0"} dias em ${moment(this.rule.tollLimitDate).format("DD/MM/YYYY")}`;
      }

      if (this.rule.ruleLimitDate) {
        const { years, months, days } = getTime(workRecords, moment(this.rule.ruleLimitDate), field, this.rule.gender, regimeDate);

        return `${years || "0"} anos, ${months || "0"} meses e ${days || "0"} dias em ${moment(this.rule.ruleLimitDate).format("DD/MM/YYYY")}`;
      }

      const { years, months, days } = getTime(workRecords, null, field, this.rule.gender, regimeDate);

      return `atual ${years || "0"} anos, ${months || "0"} meses e ${days || "0"} dias`;
    },

    positionTime: function () {
      const workRecords = this.rule.workHistory;
      const regimeDate = this.rule?.regimeDate;

      if (this.rule.ruleLimitDate) {
        const { years, months, days } = getTime(workRecords, moment(this.rule.ruleLimitDate), "isCurrentPosition", this.rule.gender, regimeDate);

        return `${years || "0"} anos, ${months || "0"} meses e ${days || "0"} dias em ${moment(this.rule.ruleLimitDate).format("DD/MM/YYYY")}`;
      }

      const { years, months, days } = getTime(workRecords, null, "isCurrentPosition", this.rule.gender, regimeDate);

      return `atual ${years || "0"} anos, ${months || "0"} meses e ${days || "0"} dias`;
    },

    publicServiceTime: function () {
      const workRecords = this.rule.workHistory;
      const regimeDate = this.rule?.regimeDate;

      if (this.rule.ruleLimitDate) {
        const { years, months, days } = getTime(workRecords, moment(this.rule.ruleLimitDate), "isPublicService", this.rule.gender, regimeDate);

        return `${years || "0"} anos, ${months || "0"} meses e ${days || "0"} dias em ${moment(this.rule.ruleLimitDate).format("DD/MM/YYYY")}`;
      }

      const { years, months, days } = getTime(workRecords, null, "isPublicService", this.rule.gender, regimeDate);

      return `atual ${years || "0"} anos, ${months || "0"} meses e ${days || "0"} dias`;
    },

    carreerTime: function () {
      const workRecords = this.rule.workHistory;
      const regimeDate = this.rule?.regimeDate;

      if (this.rule.ruleLimitDate) {
        const { years, months, days } = getTime(workRecords, moment(this.rule.ruleLimitDate), "isCurrentCareer", this.rule.gender, regimeDate);

        return `${years || "0"} anos, ${months || "0"} meses e ${days || "0"} dias em ${moment(this.rule.ruleLimitDate).format("DD/MM/YYYY")}`;
      }

      const { years, months, days } = getTime(workRecords, null, "isCurrentCareer", this.rule.gender, regimeDate);

      return `atual ${years || "0"} anos, ${months || "0"} meses e ${days || "0"} dias`;
    },

    specialTime: function () {
      const workRecords = this.rule.workHistory;
      const regimeDate = this.rule?.regimeDate;

      let field = "isSpecialTime";
      if (this.rule.necessarySpecialLevel) {
        field += `-${this.rule.necessarySpecialLevel}`;
      }

      if (this.rule.ruleLimitDate) {
        const specialTime15Geral = getTime(workRecords, moment(this.rule.ruleLimitDate), 'isSpecialTime-15 anos', this.rule.gender, regimeDate);
        const specialTime20Geral = getTime(workRecords, moment(this.rule.ruleLimitDate), 'isSpecialTime-20 anos', this.rule.gender, regimeDate);
        const specialTime25Geral = getTime(workRecords, moment(this.rule.ruleLimitDate), 'isSpecialTime-25 anos', this.rule.gender, regimeDate);
        const mainSpecialTime = getMainSpecialTimeWithConversion(specialTime15Geral, specialTime20Geral, specialTime25Geral);

        if (mainSpecialTime.mainSpecialLevel && mainSpecialTime.mainSpecialLevel == this.rule.necessarySpecialLevel) {
          return `${mainSpecialTime.years || "0"} anos, ${mainSpecialTime.months || "0"} meses e ${mainSpecialTime.days || "0"} dias em ${moment(this.rule.ruleLimitDate).format("DD/MM/YYYY")}`;
        }

        const { years, months, days } = getTime(workRecords, moment(this.rule.ruleLimitDate), field, this.rule.gender, regimeDate);

        return `${years || "0"} anos, ${months || "0"} meses e ${days || "0"} dias em ${moment(this.rule.ruleLimitDate).format("DD/MM/YYYY")}`;
      }

      const specialTime15Geral = getTime(workRecords, null, 'isSpecialTime-15 anos', this.rule.gender, regimeDate);
      const specialTime20Geral = getTime(workRecords, null, 'isSpecialTime-20 anos', this.rule.gender, regimeDate);
      const specialTime25Geral = getTime(workRecords, null, 'isSpecialTime-25 anos', this.rule.gender, regimeDate);
      const mainSpecialTime = getMainSpecialTimeWithConversion(specialTime15Geral, specialTime20Geral, specialTime25Geral);

      if (mainSpecialTime.mainSpecialLevel && mainSpecialTime.mainSpecialLevel == this.rule.necessarySpecialLevel) {
        return `atual ${mainSpecialTime.years || "0"} anos, ${mainSpecialTime.months || "0"} meses e ${mainSpecialTime.days || "0"} dias`;
      }

      const { years, months, days } = getTime(workRecords, null, field, this.rule.gender, regimeDate);

      return `atual ${years || "0"} anos, ${months || "0"} meses e ${days || "0"} dias`;
    },

    policeTime: function () {
      const workRecords = this.rule.workHistory;
      const regimeDate = this.rule?.regimeDate;

      if (this.rule.ruleLimitDate) {
        const { years, months, days } = getTime(workRecords, moment(this.rule.ruleLimitDate), "isPoliceTime", this.rule.gender, regimeDate);

        return `${years || "0"} anos, ${months || "0"} meses e ${days || "0"} dias em ${moment(this.rule.ruleLimitDate).format("DD/MM/YYYY")}`;
      }

      const { years, months, days } = getTime(workRecords, null, "isPoliceTime", this.rule.gender, regimeDate);

      return `atual ${years || "0"} anos, ${months || "0"} meses e ${days || "0"} dias`;
    },

    remunerations: function () {
      const workRecords = this.rule.workHistory;

      let remunerations = 0;

      workRecords
      .filter(workRecord => workRecord.isQualifyingPeriod)
      .filter(workRecord => this.rule.remunerationType ? workRecord[this.rule.remunerationType] : true)
      .forEach((workRecord) => {
        const startDate = moment(workRecord.ingressDate);
        const endDate = moment(workRecord.egressDate);
        
        if (startDate.isValid() && endDate.isValid()) {
          const remunerationsCount = this.countMonthsInclusive(
            startDate.format("DD/MM/YYYY"), 
            endDate.format("DD/MM/YYYY")
          );
          remunerations += remunerationsCount;
        }
      });

      const limitDate = this.rule.ruleLimitDate ? moment(this.rule.ruleLimitDate).format("DD/MM/YYYY") : moment().format("DD/MM/YYYY");
      return `${remunerations} meses em ${limitDate}`;
    },

    remunerationType: function () {
      if (!this.rule.remunerationType) return '';

      if (this.rule.remunerationType === 'isRuralTime') {
        return '(Rural)';
      }

      return '';
    },

    teachingTime: function () {
      const workRecords = this.rule.workHistory;
      const regimeDate = this.rule?.regimeDate;

      if (this.rule.tollLimitDate) {
        const { years, months, days } = getTime(workRecords, moment(this.rule.tollLimitDate), "isTeaching", this.rule.gender, regimeDate);

        return `${years || "0"} anos, ${months || "0"} meses e ${days || "0"} dias em ${moment(this.rule.tollLimitDate).format("DD/MM/YYYY")}`;
      }

      if (this.rule.isTollAtRegime) {
        const { years, months, days } = getTime(workRecords, moment(this.rule.regimeDate), "isTeaching", this.rule.gender, regimeDate);

        return `${years || "0"} anos, ${months || "0"} meses e ${days || "0"} dias em ${moment(this.rule.regimeDate).format("DD/MM/YYYY")}`;
      }

      if (this.rule.ruleLimitDate) {
        const { years, months, days } = getTime(workRecords, moment(this.rule.ruleLimitDate), "isTeaching", this.rule.gender, regimeDate);

        return `${years || "0"} anos, ${months || "0"} meses e ${days || "0"} dias em ${moment(this.rule.ruleLimitDate).format("DD/MM/YYYY")}`;
      }

      const { years, months, days } = getTime(workRecords, null, "isTeaching", this.rule.gender, regimeDate);

      return `atual ${years || "0"} anos, ${months || "0"} meses e ${days || "0"} dias`;
    },

    // points: function () {
    //   const isTeaching = this.rule?.necessaryTeachingTime > 0;

    //   const ageInDays = moment(this.rule?.calculationDate).diff(moment(this.rule?.customer.birthDate), 'days');

    //   let totalContribuiton = 0;

    //   if (isTeaching) {
    //     const { teachingTimeYears, teachingTimeMonths, teachingTimeDays } = this.rule?.calculation;

    //     totalContribuiton = (teachingTimeDays + (teachingTimeMonths * 30) + (teachingTimeYears * 365));
    //   } else {
    //     const { contributionTimeYears, contributionTimeMonths, contributionTimeDays } = this.rule?.calculation;

    //     totalContribuiton = (contributionTimeDays + (contributionTimeMonths * 30) + (contributionTimeYears * 365));
    //   }

    //   const totalPointsInDays = ageInDays + totalContribuiton;
    //   const totalPoints = Math.floor(totalPointsInDays / 365);

    //   return totalPoints;
    // },
    
    points: function () {
      // Valor canônico do motor (política Londrina/CM). Consome por PRESENÇA do
      // campo (undefined = fora da política → cálculo legado abaixo; zero é
      // válido, por isso não se testa truthiness). Garante tela = motor = PDF.
      if (this.rule.currentPoints !== undefined && this.rule.currentPoints !== null) {
        return this.rule.currentPoints;
      }
      const workRecords = this.rule.workHistory;
      let field = null;
      const isDisabledRule = this.rule.modality.includes("Deficiência");
      if (isDisabledRule) {
        let level = null;
        if (this.rule.modality.includes("Leve")) level = "low";
        if (this.rule.modality.includes("Moderada")) level = "medium";
        if (this.rule.modality.includes("Grave")) level = "high";
        field = "isDisabled";
        if (level) {
          field = `${field}-${level}`;
        }
      }

      let years, months, days

      if (this.rule.tollLimitDate) {
        ({ years, months, days } =
          getTime(workRecords, moment(this.rule.tollLimitDate), field, this.rule.gender, this.rule?.regimeDate, true));
      } else if (this.rule.necessaryPoints > 0 && this.rule.necessarySpecialTime > 0) {
        ({ years, months, days } =
        getTime(workRecords, moment(this.rule.ruleLimitDate), field, this.rule.gender, this.rule?.regimeDate, true));
      } else if (this.rule.ruleLimitDate && !this.rule?.necessaryTeachingTime > 0) {
        ({ years, months, days } =
          getTime(workRecords, moment(this.rule.ruleLimitDate), field, this.rule.gender, this.rule?.regimeDate));
      } else if (this.rule?.necessaryTeachingTime > 0) {
        ({ years, months, days } =
          getTime(workRecords, moment(this.rule.ruleLimitDate), field, this.rule.gender, this.rule?.regimeDate, true));
      } else {
        ({ years, months, days } =
          getTime(workRecords, null, field, this.rule.gender, this.rule?.regimeDate, true));
      }
      
      const totalContribuiton = (days + (months * 30) + (years * 365));
      const ageInDays = moment(this.rule.ruleLimitDate).diff(moment(this.rule?.customer.birthDate), 'days');
      const totalPointsInDays = ageInDays + totalContribuiton;
      const totalPoints = Math.floor(totalPointsInDays / 365);
      return totalPoints;
    },

    ageToRetire: function () {
      if (!this.rule.ageToRetire) return false;
      const date = moment(this.rule.ageToRetire);
      return date.isValid() ? date : false;
    },

    remunerationsToRetire: function () {
      if (!this.rule.remunerationsToRetire) return false;
      const date = moment(this.rule.remunerationsToRetire);
      return date.isValid() ? date : false;
    },

    contributionToRetire: function () {
      if (!this.rule.contributionToRetire) return false;
      const date = moment(this.rule.contributionToRetire);
      return date.isValid() ? date : false;
    },

    positionToRetire: function () {
      if (!this.rule.positionToRetire) return false;
      const date = moment(this.rule.positionToRetire);
      return date.isValid() ? date : false;
    },

    publicServiceToRetire: function () {
      if (!this.rule.publicServiceToRetire) return false;
      const date = moment(this.rule.publicServiceToRetire);
      return date.isValid() ? date : false;
    },

    teachingToRetire: function () {
      if (!this.rule.teachingToRetire) return false;
      const date = moment(this.rule.teachingToRetire);
      return date.isValid() ? date : false;
    },

    carreerToRetire: function () {
      if (!this.rule.carreerToRetire) return false;
      const date = moment(this.rule.carreerToRetire);
      return date.isValid() ? date : false;
    },

    specialTimeToRetire: function () {
      if (!this.rule.specialTimeToRetire) return false;
      const date = moment(this.rule.specialTimeToRetire);
      return date.isValid() ? date : false;
    },

    policeToRetire: function () {
      if (!this.rule.policeToRetire) return false;
      const date = moment(this.rule.policeToRetire);
      return date.isValid() ? date : false;
    },

    hadBirthday: function () {
      const now = moment();

      const birthday = moment(this.rule?.customer?.birthDate);

      const currentMonth = now.month();
      const birthdayMonth = birthday.month();

      if (currentMonth > birthdayMonth) return true;

      if (birthdayMonth > currentMonth) return false;

      const currentDay = now.date();
      const birthdayDay = birthday.date();

      if (currentDay >= birthdayDay) return true;

      return false;
    },

    pointsToRetire: function () {
      if (!this.rule.pointsToRetire) return false;
      const date = moment(this.rule.pointsToRetire);
      return date.isValid() ? date : false;
    },

    reachedContributionDate: function () {
      return calculateReachedContributionDate(this.rule);
    },

    reachedContributionDateIgnoringStartDate: function () {
      if (this.isExpiredNotApplicable) return null; // U2
      // Fonte preferida: data REAL calculada pelo motor (elimina o 4º motor de
      // datas e o drift tela×PDF). GATE preservado: futura/inaplicável não exibe
      // "Completou em" (condição do review Codex). Fallback: recompute legado.
      if (((!this.isFuture && !this.isNotApplicable) || this.rule?.exclusion?.source === 'applicable')
          && this.rule?.reachedContributionDate) {
        return moment(this.rule.reachedContributionDate);
      }
      return calculateReachedContributionDateIgnoringStartDate(this.rule);
    },

    reachedTeachingDate: function () {
      return calculateReachedTeachingDate(this.rule);
    },

    reachedTeachingDateIgnoringStartDate: function () {
      if (this.isExpiredNotApplicable) return null; // U2
      // Fonte preferida: data REAL calculada pelo motor (elimina o 4º motor de
      // datas e o drift tela×PDF). GATE preservado: futura/inaplicável não exibe
      // "Completou em" (condição do review Codex). Fallback: recompute legado.
      if (((!this.isFuture && !this.isNotApplicable) || this.rule?.exclusion?.source === 'applicable')
          && this.rule?.reachedTeachingDate) {
        return moment(this.rule.reachedTeachingDate);
      }
      if ((!this.isFuture && !this.isNotApplicable) || this.rule?.exclusion?.source === 'applicable') {
        const regimeDate = this.rule?.regimeDate;
        const limitDate = this.rule.ruleLimitDate ? moment(this.rule.ruleLimitDate) : moment();

        let necessaryTeachingTime = this.rule.increasedNecessaryTeachingTime || this.rule.necessaryTeachingTime || 0;
        let necessaryTeachingTimeMonths = this.rule.increasedNecessaryTeachingTimeMonths || 0;
        let necessaryTeachingTimeDays = this.rule.increasedNecessaryTeachingTimeDays || 0;
        let reachedDate = null;

        let tollDate = this.rule?.tollDate ? moment(this.rule.tollDate) : null;

        if (this.rule?.isTollAtRegime) {
          tollDate = moment(regimeDate);
        }

        if (necessaryTeachingTime) {
          if ((this.rule?.modality).toUpperCase().includes("PEDÁGIO")) {
            const workRecords = this.rule?.workHistory || [];
            const {
              days: teachingTimeDaysIn2019,
              months: teachingTimeMonthsIn2019,
              years: teachingTimeYearsIn2019
            } = getTime(workRecords, moment(limitDate), 'isTeaching', this.rule?.gender, regimeDate);

            let tollYears = 0;
            let tollMonths = 0;
            let tollDays = 0;

            if (teachingTimeYearsIn2019 < necessaryTeachingTime) {
              tollYears = necessaryTeachingTime - teachingTimeYearsIn2019;
              tollMonths = teachingTimeMonthsIn2019;
              tollDays = teachingTimeDaysIn2019;

              if (tollMonths > 0 || tollDays > 0) {
                tollYears -= 1;
              }

              if (tollMonths > 0) {
                tollMonths = 12 - tollMonths;
              }

              if (tollDays > 0) {
                tollMonths -= 1;
                tollDays = 30 - tollDays;
              }

              // Verificar se deve usar tabela específica de pedágio
              let tollPercentage = 2; // Padrão 100% (multiplicação por 2)
              if (this.rule?.useTollTable) {
                const specificPercentage = this.specificTolls(
                  this.rule?.regime?.name || this.rule?.calculation?.regime,
                  this.rule?.tollPercentage || 100,
                  necessaryContributionTime,
                  contributionTimeYearsIn2019,
                  contributionTimeMonthsIn2019,
                  contributionTimeDaysIn2019
                );
                tollPercentage = specificPercentage / 100;
              }

              tollYears *= tollPercentage;
              tollMonths *= tollPercentage;
              tollDays *= tollPercentage;

              if (tollDays >= 30) {
                const daysToMonths = Math.floor(tollDays / 30);

                tollDays -= (daysToMonths * 30);
                tollMonths += daysToMonths;
              }

              if (tollMonths >= 12) {
                const monthsToYears = Math.floor(tollMonths / 12);

                tollMonths -= (monthsToYears * 12);
                tollYears += monthsToYears;
              }

              let finalDate = moment(limitDate)
                .add(tollYears, 'years')
                .add(tollMonths, 'months')
                .add(tollDays, 'days')

              reachedDate = finalDate;
            } else {
              const { 
                days: teachingTimeDaysAtToll,
                months: teachingTimeMonthsAtToll,
                years: teachingTimeYearsAtToll
              } = getTime(workRecords, moment(tollDate || limitDate), 'isTeaching', this.rule.gender, regimeDate);

              let tollPercentage = this.rule?.tollPercentage || 100;
              if (this.rule?.useTollTable) {
                const specificPercentage = specificTolls(
                  this.rule?.regime?.name || this.rule?.calculation?.regime,
                  this.rule?.tollPercentage || 100,
                  teachingTimeYearsAtToll,
                  teachingTimeYearsAtToll,
                  teachingTimeMonthsAtToll,
                  teachingTimeDaysAtToll
                );
                tollPercentage = specificPercentage;
              }

              const { 
                years: tollYearsAtToll, 
                months: tollMonthsAtToll, 
                days: tollDaysAtToll 
              } = calculateToll(
                this.rule.necessaryTeachingTime, 
                {
                  contributionYears: teachingTimeYearsAtToll,
                  contributionMonths: teachingTimeMonthsAtToll,
                  contributionDays: teachingTimeDaysAtToll
                }, 
                tollPercentage / 100
              );

              let finalDate

              if(tollYearsAtToll >= necessaryTeachingTime) {

                finalDate = getTime(workRecords, moment(limitDate), 'isTeaching', this.rule.gender, regimeDate, false, {
                  years: tollYearsAtToll,
                  months: tollMonthsAtToll,
                  days: tollDaysAtToll
                });
                
                reachedDate = moment(finalDate.completionDate);
              } else {
                finalDate = getTime(workRecords, moment(tollDate || limitDate), 'isTeaching', this.rule.gender, regimeDate, false, {
                  years: necessaryTeachingTime,
                  months: 0,
                  days: 0
                });
                reachedDate = moment(finalDate.completionDate);
              }

            }
          } else {
            let finalDate = getTime(this.rule?.workHistory, moment(limitDate), 'isTeaching', this.rule.gender, regimeDate, false, {
              years: necessaryTeachingTime,
              months: 0,
              days: 0
            });
            finalDate = moment(finalDate.completionDate);

        if (this.rule?.ruleStartDate && moment(this.rule.ruleStartDate).isAfter(finalDate)) {
          finalDate = moment(this.rule.ruleStartDate);
        }

        reachedDate = finalDate;
          }
        }

        return reachedDate;
      }
      
      return null;
    },

    reachedAgeDate: function () {
      return calculateReachedAgeDate(this.rule, this.reachedContributionDate, this.reachedTeachingDate);
    },

    reachedAgeDateIgnoringStartDate: function () {
      if (this.isExpiredNotApplicable) return null; // U2

      // H3 v2 (#432, Campo Grande - MS, 11/09, item 7-1): o motor publica
      // reductorAgeReachedAt quando a IDADE do redutor (já reduzida pelo bestN
      // ótimo) foi cumprida antes do limite do cálculo, pro regime no mapa
      // reductorAgeOwnDate. A tela mostra "Completou em <data>" mesmo em regra
      // FUTURA — fura o gate de futura SÓ por presença deste campo (não muda
      // isFuture/isNotApplicable em nenhum outro bloco). Ausente (todo o resto,
      // fora do mapa) => comportamento de hoje, byte a byte.
      if (this.rule?.reductorAgeReachedAt) {
        const reductorAgeReachedAtDate = moment(this.rule.reductorAgeReachedAt);
        if (reductorAgeReachedAtDate.isValid()) return reductorAgeReachedAtDate;
      }

      // #524 R4 (achado Codex 7620b5ec) — "Completou em" da idade tem que
      // corresponder à idade EXIBIDA (displayNecessaryAge/T3), não à idade
      // decisória (rule.reachedAgeDate, tabela do ano do CÁLCULO no RGPS). Fonte
      // NOVA preferida: displayAgeCompletedAt (R3, só RGPS + tabela progressiva).
      // Ausente (RPPS, sem tabela, redutor) => cai no comportamento de hoje.
      if (((!this.isFuture && !this.isNotApplicable) || this.rule?.exclusion?.source === 'applicable')
          && this.rule?.displayAgeCompletedAt) {
        return moment(this.rule.displayAgeCompletedAt);
      }
      // Fonte preferida: data REAL calculada pelo motor (elimina o 4º motor de
      // datas e o drift tela×PDF). GATE preservado: futura/inaplicável não exibe
      // "Completou em" (condição do review Codex). Fallback: recompute legado.
      if (((!this.isFuture && !this.isNotApplicable) || this.rule?.exclusion?.source === 'applicable')
          && this.rule?.reachedAgeDate) {
        return moment(this.rule.reachedAgeDate);
      }
      if ((!this.isFuture && !this.isNotApplicable) || this.rule?.exclusion?.source === 'applicable') {
        const regimeDate = this.rule?.regimeDate;
        const limitDate = this.rule.ruleLimitDate ? moment(this.rule.ruleLimitDate) : moment();
        
        let necessaryAge = this.rule.necessaryAge || 0;
        let necessaryAgeMonths = 0;
        let necessaryAgeDays = 0;
        let reachedDate = null;

        if (necessaryAge) {
          if (this.rule?.useTollTable && (this.rule.decreasedNecessaryAge || this.rule.decreasedNecessaryAgeMonths || this.rule.decreasedNecessaryAgeDays)) {
            necessaryAge = this.rule.decreasedNecessaryAge || necessaryAge;
            necessaryAgeMonths = this.rule.decreasedNecessaryAgeMonths || necessaryAgeMonths;
            necessaryAgeDays = this.rule.decreasedNecessaryAgeDays || necessaryAgeDays;
          }

          if (this.rule?.modality.includes("Com Redutor") || this.rule?.hasReductor) {
            let reachedContribution = moment(this.rule.reachedContributionDate) || this.reachedTeachingDate || null;

            const start = moment(this.rule?.customer?.birthDate);
            let end = moment(reachedContribution);
            let age = end.diff(start, 'years');

            do {
              if (age >= necessaryAge) {
                let finalDate = moment(reachedContribution);
                
                const finalYear = moment(reachedContribution).year();
                const birthdayBefore = moment(this.rule?.customer?.birthDate).year(finalYear);

                if (birthdayBefore.isBefore(reachedContribution)) {
                  finalDate = birthdayBefore;
                }

                if (birthdayBefore.isAfter(reachedContribution)) {
                  finalDate = birthdayBefore.subtract(1, 'year');
                }

                reachedDate = finalDate;

                break;
              }

              if (age < necessaryAge) {
                reachedContribution.add(1, 'year');
                necessaryAge -= 1; 
                age += 1; 
                this.rule.descreasedAge = necessaryAge;
              }
            } while (true);
          } else {
            let years = 0;
            let months = 0;
            let days = 0;

            const start = moment(this.rule?.customer?.birthDate);
  
            let end = moment(limitDate);
  
            years = end.diff(start, 'years');
            end = end.subtract(years, 'years');
            months = end.diff(start, 'months');
            end = end.subtract(months, 'months');
            days = end.diff(start, 'days');
  
            const extraTime = years - necessaryAge;
            
            const date = moment(limitDate);
            
            let finalDate = date
              .subtract(extraTime || 0, 'years')
              .subtract(months, 'months')
              .subtract(days, 'days')
              .add(necessaryAgeMonths, 'months')
              .add(necessaryAgeDays, 'days')
  
            reachedDate = finalDate;
          }
        }

        return reachedDate;
      }

      return null;
    },

    reachedPositionDate: function () {
      return calculateReachedPositionDate(this.rule);
    },

    reachedPositionDateIgnoringStartDate: function () {
      if (this.isExpiredNotApplicable) return null; // U2
      // Fonte preferida: data REAL calculada pelo motor (elimina o 4º motor de
      // datas e o drift tela×PDF). GATE preservado: futura/inaplicável não exibe
      // "Completou em" (condição do review Codex). Fallback: recompute legado.
      if (((!this.isFuture && !this.isNotApplicable) || this.rule?.exclusion?.source === 'applicable')
          && this.rule?.reachedPositionDate) {
        return moment(this.rule.reachedPositionDate);
      }
      if ((!this.isFuture && !this.isNotApplicable) || this.rule?.exclusion?.source === 'applicable') {
        const regimeDate = this.rule?.regimeDate;
        const limitDate = this.rule.ruleLimitDate ? moment(this.rule.ruleLimitDate) : moment();
        
        let necessaryPositionTime = this.rule.necessaryPositionTime || 0;
        let reachedDate = null;

        if (necessaryPositionTime) {
          const { 
            years: positionTimeYears, 
            months: positionTimeMonths, 
            days: positionTimeDays 
          } = getTime(this.rule?.workHistory, limitDate, 'isCurrentPosition', this.rule?.gender, regimeDate);

          const extraTime = positionTimeYears - necessaryPositionTime;

          let finalDate = moment(limitDate).subtract(extraTime || 0, 'years')
            .subtract(positionTimeMonths || 0, 'months')
            .subtract(positionTimeDays || 0, 'days')

          reachedDate = finalDate;
        }

        return reachedDate;
      }

      return null
    },

    reachedPublicServiceDate: function () {
      return calculateReachedPublicServiceDate(this.rule);
    },

    reachedPublicServiceDateIgnoringStartDate: function () {
      if (this.isExpiredNotApplicable) return null; // U2
      // Fonte preferida: data REAL calculada pelo motor (elimina o 4º motor de
      // datas e o drift tela×PDF). GATE preservado: futura/inaplicável não exibe
      // "Completou em" (condição do review Codex). Fallback: recompute legado.
      if (((!this.isFuture && !this.isNotApplicable) || this.rule?.exclusion?.source === 'applicable')
          && this.rule?.reachedPublicServiceDate) {
        return moment(this.rule.reachedPublicServiceDate);
      }
      if ((!this.isFuture && !this.isNotApplicable) || this.rule?.exclusion?.source === 'applicable') {
        const regimeDate = this.rule?.regimeDate;
        const limitDate = this.rule.ruleLimitDate ? moment(this.rule.ruleLimitDate) : moment();
        
        let necessaryPublicServiceTime = this.rule.necessaryPublicServiceTime || 0;
        let reachedDate = null;

        if (necessaryPublicServiceTime) {
          const {
            years: publicServiceTimeYears,
            months: publicServiceTimeMonths,
            days: publicServiceTimeDays
          } = getTime(this.rule?.workHistory, limitDate, 'isPublicService', this.rule?.gender, regimeDate);

          const extraTime = publicServiceTimeYears - necessaryPublicServiceTime;

          let finalDate = moment(limitDate).subtract(extraTime || 0, 'years')
            .subtract(publicServiceTimeMonths || 0, 'months')
            .subtract(publicServiceTimeDays || 0, 'days')

          reachedDate = finalDate;
        }

        return reachedDate;
      }

      return null;
    },

    reachedPoliceDate: function () {
      return calculateReachedPoliceDate(this.rule);
    },

    reachedPoliceDateIgnoringStartDate: function () {
      if (this.isExpiredNotApplicable) return null; // U2
      // Fonte preferida: data REAL calculada pelo motor (elimina o 4º motor de
      // datas e o drift tela×PDF). GATE preservado: futura/inaplicável não exibe
      // "Completou em" (condição do review Codex). Fallback: recompute legado.
      if (((!this.isFuture && !this.isNotApplicable) || this.rule?.exclusion?.source === 'applicable')
          && this.rule?.reachedPoliceDate) {
        return moment(this.rule.reachedPoliceDate);
      }
      if ((!this.isFuture && !this.isNotApplicable) || this.rule?.exclusion?.source === 'applicable') {
        const regimeDate = this.rule?.regimeDate;
        const limitDate = this.rule.ruleLimitDate ? moment(this.rule.ruleLimitDate) : moment();
        
        let necessaryPoliceTime = this.rule.necessaryPoliceTime || 0;
        let reachedDate = null;

        if (necessaryPoliceTime) {
          const {
            years: policeTimeYears,
            months: policeTimeMonths,
            days: policeTimeDays
          } = getTime(this.rule?.workHistory, limitDate, 'isPoliceTime', this.rule?.gender, regimeDate);

          const extraTime = policeTimeYears - necessaryPoliceTime;

          let finalDate = moment(limitDate).subtract(extraTime || 0, 'years')
            .subtract(policeTimeMonths || 0, 'months')
            .subtract(policeTimeDays || 0, 'days')

          reachedDate = finalDate;
        }

        return reachedDate;
      }

      return null;
    },

    reachedRemunerationsDate: function () {
      if ((!this.isFuture && !this.isNotApplicable) || this.rule?.exclusion?.source === 'applicable') {
        const regimeDate = this.rule?.regimeDate;
        const limitDate = this.rule.ruleLimitDate ? moment(this.rule.ruleLimitDate) : moment();

        return computeReachedRemunerationsDate(this.rule, this.countMonthsInclusiveToLimit);
      }

      return null;
    },

    reachedCarreerDate: function () {
      return calculateReachedCarreerDate(this.rule);
    },

    reachedCarreerDateIgnoringStartDate: function () {
      if (this.isExpiredNotApplicable) return null; // U2
      // Fonte preferida: data REAL calculada pelo motor (elimina o 4º motor de
      // datas e o drift tela×PDF). GATE preservado: futura/inaplicável não exibe
      // "Completou em" (condição do review Codex). Fallback: recompute legado.
      if (((!this.isFuture && !this.isNotApplicable) || this.rule?.exclusion?.source === 'applicable')
          && this.rule?.reachedCarreerDate) {
        return moment(this.rule.reachedCarreerDate);
      }
      if ((!this.isFuture && !this.isNotApplicable) || this.rule?.exclusion?.source === 'applicable') {
        const regimeDate = this.rule?.regimeDate;
        const limitDate = this.rule.ruleLimitDate ? moment(this.rule.ruleLimitDate) : moment();

        let necessaryCarreerTime = this.rule.necessaryCarreerTime || 0;
        let reachedDate = null;

        if (necessaryCarreerTime) {
          const {
            years: carreerTimeYears,
            months: carreerTimeMonths,
            days: carreerTimeDays
          } = getTime(this.rule?.workHistory, limitDate, 'isCurrentCareer', this.rule?.gender, regimeDate);

          const extraTime = carreerTimeYears - necessaryCarreerTime;

          let finalDate = moment(limitDate).subtract(extraTime || 0, 'years')
            .subtract(carreerTimeMonths || 0, 'months')
            .subtract(carreerTimeDays || 0, 'days')

          reachedDate = finalDate;
        }

        return reachedDate;
      }

      return null;
    },

    reachedSpecialTimeDate: function () {
      return calculateReachedSpecialTimeDate(this.rule);
    },

    reachedSpecialTimeDateIgnoringStartDate: function () {
      if (this.isExpiredNotApplicable) return null; // U2
      // Fonte preferida: data REAL calculada pelo motor (elimina o 4º motor de
      // datas e o drift tela×PDF). GATE preservado: futura/inaplicável não exibe
      // "Completou em" (condição do review Codex). Fallback: recompute legado.
      if (((!this.isFuture && !this.isNotApplicable) || this.rule?.exclusion?.source === 'applicable')
          && this.rule?.reachedSpecialDate) {
        return moment(this.rule.reachedSpecialDate);
      }
      if ((!this.isFuture && !this.isNotApplicable) || this.rule?.exclusion?.source === 'applicable') {
        const regimeDate = this.rule?.regimeDate;
        const limitDate = this.rule.ruleLimitDate ? moment(this.rule.ruleLimitDate) : moment();

        let field = "isSpecialTime";
        if (this.rule.necessarySpecialLevel) {
          field += `-${this.rule.necessarySpecialLevel}`;
        }
        
        let necessarySpecialTime = this.rule.necessarySpecialTime || 0;
        let reachedDate = null;

        if (necessarySpecialTime) {
          const workRecords = this.rule?.workHistory || [];

          let specialTimeYears = 0;
          let specialTimeMonths = 0;
          let specialTimeDays = 0;

          const {
            days: specialTimeDaysTime,
            months: specialTimeMonthsTime,
            years: specialTimeYearsTime
          } = getTime(workRecords, this.rule?.ruleLimitDate, field, this.rule?.gender, regimeDate);

          const specialTime15Geral = getTime(workRecords, this.rule?.ruleLimitDate, 'isSpecialTime-15 anos', this.rule?.gender, regimeDate);
          const specialTime20Geral = getTime(workRecords, this.rule?.ruleLimitDate, 'isSpecialTime-20 anos', this.rule?.gender, regimeDate);
          const specialTime25Geral = getTime(workRecords, this.rule?.ruleLimitDate, 'isSpecialTime-25 anos', this.rule?.gender, regimeDate);
          const mainSpecialTime = getMainSpecialTimeWithConversion(specialTime15Geral, specialTime20Geral, specialTime25Geral);

          if (mainSpecialTime.mainSpecialLevel && mainSpecialTime.mainSpecialLevel == this.rule.necessarySpecialLevel) {
            specialTimeYears = mainSpecialTime.years;
            specialTimeMonths = mainSpecialTime.months;
            specialTimeDays = mainSpecialTime.days;
          } else {
            specialTimeYears = specialTimeYearsTime;
            specialTimeMonths = specialTimeMonthsTime;
            specialTimeDays = specialTimeDaysTime;
          }

          const extraTime = specialTimeYears - necessarySpecialTime;

          const date = this.rule?.ruleLimitDate ? moment(this.rule?.ruleLimitDate) : moment();

          let finalDate = date.subtract(extraTime || 0, 'years')
            .subtract(specialTimeMonths || 0, 'months')
            .subtract(specialTimeDays || 0, 'days')

          reachedDate = finalDate;
        }

        return reachedDate;
      }

      return null;
    },

    reachedDates: function () {
      return calculateReachedDates(this.rule);
    },

    // U2 (invariante de UX, 06/07/2026): regra INAPLICÁVEL com vigência VENCIDA
    // não exibe header "Possível aposentadoria", projeções nem "Completou em"
    // recalculados pelo fallback do front (ex.: União "Por pontos (2019)" N
    // mostrando cumprimento 01/08/2023, após o fim da vigência 31/12/2021).
    isExpiredNotApplicable: function () {
      return !!(this.isNotApplicable && this.rule?.ruleEndDate
        && moment(this.rule.ruleEndDate).isBefore(moment(), 'day'));
    },

    reachedPointsDate: function () {
      if (this.isExpiredNotApplicable) return null; // U2
      const shouldHideReachedPointsDateForFutureRule =
        this.isFuture &&
        this.rule?.isPointsRule &&
        !!this.rule?.pointsToRetire;

      if (shouldHideReachedPointsDateForFutureRule) {
        return null;
      }

      if (this.rule?.reachedPointsDate) {
        return moment(this.rule.reachedPointsDate);
      }

      return calculateReachedPointsDate(this.rule, this.reachedDates);
    },

    retiredDate: function () {
      if (this.isExpiredNotApplicable) return null; // U2
      if (this.rule._calculatedRetiredDate) {
        return this.rule._calculatedRetiredDate;
      }
      // Fonte preferida: applicableAt do MOTOR (max de TODAS as aplicabilidades,
      // incl. magistério/especial/polícia). O recompute legado do front produzia
      // "Possível aposentadoria em" ANTERIOR ao cumprimento do magistério
      // (10/06/2016 < 19/06/2016 — smoke 04/07). Gate: só regras aplicáveis.
      // #524 item (2) — Ju 14/08: «a "Possível aposentadoria em" SEMPRE, sem
      // exceções, vai ser a data em que a pessoa cumpriu o ÚLTIMO requisito que
      // faltava para cumprir todos os requisitos».
      // `lastRequirementAt` é o `applicableAt` SEM a substituição artificial que o
      // ramo RGPS do motor fazia (trocar a vigência do banco pela data do CÁLCULO),
      // que prendia o cabeçalho de toda regra RGPS ao dia em que o cálculo rodou.
      // Para RPPS os dois campos são iguais por construção -> nenhum município muda.
      // `applicableAt` continua intocado e segue sendo o insumo da precedência da
      // compulsória (e o fallback aqui, para regra vinda de motor antigo).
      if ((!this.isFuture && !this.isNotApplicable) || this.rule?.exclusion?.source === 'applicable') {
        if (this.rule?.lastRequirementAt) return moment(this.rule.lastRequirementAt);
        if (this.rule?.applicableAt) return moment(this.rule.applicableAt);
      }
      return calculateRetiredDate(this.rule);
    },

    whenToRetire: function () {
      if (!this.rule.whenToRetire) return false;
      const date = moment(this.rule.whenToRetire);
      return date.isValid() ? date : false;

      // const isNewRule = (this.rule.ruleStartDate);
      // const checkNewRule = (this.rule.calculation.regime !== "Sem Reforma");

      // const newRuleCondition = ((!checkNewRule) || (checkNewRule && isNewRule));

      // if (this.isFuture && newRuleCondition) {
      //   let dates = [];

      //   dates.push(this.ageToRetire);
      //   dates.push(this.contributionToRetire);
      //   dates.push(this.positionToRetire);
      //   dates.push(this.publicServiceToRetire);
      //   dates.push(this.teachingToRetire);
      //   dates.push(this.carreerToRetire);
      //   dates.push(this.pointsToRetire);

      //   dates = dates.filter(d => d);

      //   if (dates.length === 0) return false;

      //   let lastDate = moment.max(dates); 

      //   return lastDate;
      // }
      // return false
    },

    formattedRuleEndDate: function () {
      if (!this.rule.ruleEndDate) return '';
        
      const date = moment(this.rule.ruleEndDate);
      return date.isValid() ? date.format('DD/MM/YYYY') : this.rule.ruleEndDate;
    },

    formattedApplicableAt: function () {
      if (!this.rule.applicableAt) return '';
      
      const date = moment(this.rule.applicableAt);
      return date.isValid() ? date.format('DD/MM/YYYY') : this.rule.applicableAt;
    },

    pointsDisplayText: function () {
      if (this.isExpiredNotApplicable) return ''; // U2
      if (!this.rule.necessaryPoints) return '';

      if (this.rule.ruleEndDate) {
        // H2 v2 (#432, Campo Grande - MS, 11/09, item 7-2): regimes com
        // pointsLabelAtLimitDate rotulam os pontos ATUAIS (na data-limite do
        // cálculo, mesma data dos demais campos do card) em vez da data de fim
        // de vigência da regra. Fora do mapa: comportamento de hoje, intocado.
        if (this.rule.pointsLabelAtLimitDate && this.rule.ruleLimitDate) {
          return `(${this.points} em ${moment(this.rule.ruleLimitDate).format('DD/MM/YYYY')})`;
        }
        return `(${this.points} em ${this.formattedRuleEndDate})`;
      }

      return `(atual ${this.points})`;
    },
  },

  methods: {
    calculateToll(necessaryContributionYears, { contributionYears, contributionMonths, contributionDays }, multiple) {
      return calculateToll(necessaryContributionYears, { contributionYears, contributionMonths, contributionDays }, multiple);
    },

    // Função para calcular percentuais específicos de pedágio baseado no regime
    specificTolls(regimeName, percentage, necessaryYears, contributedY, contributedM, contributedD) {
      return specificTolls(regimeName, percentage, necessaryYears, contributedY, contributedM, contributedD);
    },

    calculateDate(initialDate, finalDate) {
      try {
        let initialYears = initialDate.year();
        let initialMonths = initialDate.month() + 1;
        let initialDays = initialDate.date();

        let finalYears = finalDate.year();
        let finalMonths = finalDate.month() + 1;
        let finalDays = finalDate.date();

        let years = 0;
        let months = 0;
        let days = 0;

        if (finalDays - initialDays < 0) {
          finalMonths -= 1;
          finalDays += 30;
        }

        days = finalDays - initialDays;

        if (finalMonths - initialMonths < 0) {
          finalYears -= 1;
          finalMonths += 12;
        }

        months = finalMonths - initialMonths;

        years = finalYears - initialYears;

        days += 1;

        return { days, months, years };
      } catch (error) {
        throw { days: 0, months: 0, years: 0 };
      }
    },

    openBenefitReport(futureSalaryType) {
      this.benefitReportFutureType = futureSalaryType ?? null;
      this.showBenefitReportModal = true;
    },
    handlerCloseModal() {
      this.showBenefitReportModal = false;
      this.benefitReportFutureType = null;
    },

    formatMoney(value, digits) {
      const d = digits !== undefined ? digits : 2;
      const nf = new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
      });
      return nf.format(value);
    },

    futureSalaryTypeLabel(type) {
      const labels = {
        min: 'Mínimo',
        ceiling: 'Teto',
        user_input: 'Valor informado',
        repeat_last: 'Rep. última',
        zero: 'Zerados',
      };
      return labels[type] || type;
    },

    getLastWorkRecordEgress() {
      const workRecords = this.rule?.workHistory || [];

      const lastWorkRecord = workRecords.sort((a, b) => new Date(b.egressDate) - new Date(a.egressDate))[0];

      return moment(lastWorkRecord.egressDate);
    },

    countMonthsInclusive(startDateStr, endDateStr, format = "DD/MM/YYYY") {
      const startDate = moment(startDateStr, format, true);
      const endDate   = moment(endDateStr, format, true);

      if (!startDate.isValid() || !endDate.isValid()) {
        throw new Error("Invalid date format");
      }

      const firstDate = moment.min(startDate, endDate);
      const lastDate  = moment.max(startDate, endDate);

      const monthsCount =
        (lastDate.year() - firstDate.year()) * 12 +
        (lastDate.month() - firstDate.month()) +
        1;

      return monthsCount;
    },
    
    countMonthsInclusiveToLimit(startDateStr, endDateStr, initial, limit, format = "DD/MM/YYYY") {
      const startDate = moment(startDateStr, format, true);
      const endDate   = moment(endDateStr, format, true);

      if (!startDate.isValid() || !endDate.isValid()) {
        throw new Error("Invalid date format");
      }

      const firstDate = moment.min(startDate, endDate);
      const lastDate  = moment.max(startDate, endDate);

      const totalMonths =
        (lastDate.year() - firstDate.year()) * 12 +
        (lastDate.month() - firstDate.month()) +
        1;

      let cumulative = initial || 0;
      let monthsCounted = 0;
      let reachedAt = null;

      for (let i = 0; i < totalMonths; i++) {
        cumulative += 1;
        monthsCounted += 1;
        if (cumulative >= limit) {
          reachedAt = moment(firstDate).add(i, 'months');
          break;
        }
      }

      if (reachedAt) {
        return { months: monthsCounted, date: reachedAt };
      }

      return { months: totalMonths, date: null };
    },
    
    getNecessaryPointsTable(year, month) {
      return getNecessaryPointsTable(this.rule, year, month);
    },

    getDecreasedAge() {
      const { decreasedNecessaryAge, necessaryAge } = this.rule;

      if (!decreasedNecessaryAge) return null;

      let descreasedAge = 0;
      let age = 0;

      const maxContribution = (this.rule?.gender === "Homem") ? 35 : 30;

      const limitYear = moment(this.rule?.ruleLimitDate);

      const regimeDate = getRegimeDate(this.rule?.regimeDate);

      const baseYear = moment(this.rule?.ruleLimitDate);

      do {
        let { years: contributionTimeYears } = getTime(this.rule?.workHistory, limitYear, null, this.rule?.gender, regimeDate);

        descreasedAge = necessaryAge - (contributionTimeYears - maxContribution);
        age = baseYear.diff(moment(this.rule?.customer?.birthDate), 'years');

        baseYear.subtract(1, 'year');
      } while (age > descreasedAge);

      return descreasedAge;
    },

    // getRegimeDate(regime) {
    //   const dates = {
    //     'União': '2019-11-13',
    //     'PR': '2019-12-03',
    //     'SP': '2020-03-07',
    //     'ES': '2020-07-01',
    //     'RS': '2019-12-22',
    //     'MT': '2020-08-20',
    //     'PB': '2021-07-02',
    //     'Curitiba': '2021-12-31'
    //   };

    //   let date = dates[regime];

    //   if (regime === 'Sem Reforma') date = moment().add(1, 'day').format('YYYY-MM-DD');

    //   return moment(date);
    // }
  },
}
</script>

<style lang="scss">
.no-break {
  break-inside: avoid;
}

.rule-not-applicable.bg-white-blue {
  background-color: #f7eced !important;
  border-color: #df7680 !important;
}

.rule-not-applicable {

  .border-bottom,
  .border-right {
    border-color: transparent !important;
  }

  .text-gray-400 {
    color: #ce9ca0 !important;
  }

  .text-primary,
  .text-success,
  .text-info {
    color: #c44a56 !important;
  }
}

.future-rule.bg-white-blue {
  background-color: #f5f0e7 !important;
  border-color: #d0ba90 !important;
}

.future-rule {

  .border-bottom,
  .border-right {
    border-color: transparent !important;
  }

  .text-gray-400 {
    color: #d7c4a1 !important;
  }

  .text-primary,
  .text-success,
  .text-info {
    color: #a8884b !important;
  }
}

.benefit-grid {
  gap: 0.5rem;
}

.benefit-cell {
  min-width: 7rem;
  transition: background-color 0.15s ease;
  .benefit-cell-label {
    font-size: 0.75rem;
    line-height: 1.2;
  }
  .benefit-cell-value {
    font-size: 0.85rem;
    line-height: 1.2;
  }
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
  &:focus {
    outline: 1px dotted;
  }
}
</style>

